import { Elements, ExpressCheckoutElement } from "@stripe/react-stripe-js";
import {
  StripeElementsOptions,
  AvailablePaymentMethods,
  loadStripe,
} from "@stripe/stripe-js";

import { useOrganization } from "~hooks";
import { createContext, FC, useContext, useMemo, useState } from "react";
import { Box } from "flicket-ui";
import { checkoutDebug } from "~lib/debug";
import { useStripeConfigContext } from "~lib/stripeConfigContext";
import { ExpressCheckoutModal } from "./ExpressCheckout.modal";

type PaymentMethod = keyof AvailablePaymentMethods;

const options: StripeElementsOptions = {
  mode: "payment",
  appearance: {
    theme: "stripe",
    rules: {
      ".Block": {
        margin: "0px",
        padding: "0px",
      },
    },
  },
  fonts: [
    {
      cssSrc:
        "https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap",
    },
  ],
};

interface ExpressCheckoutContextProps {
  hasPaymentMethods: boolean;
  isLoadingExpressCheckoutElement: boolean;
  availablePaymentMethods: PaymentMethod[] | undefined;
}

const ExpressCheckoutContext = createContext<
  ExpressCheckoutContextProps | undefined
>(undefined);

const ExpressCheckoutProvider: FC = ({ children }) => {
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    PaymentMethod[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const config = useStripeConfigContext();
  const { organization } = useOrganization();

  const stripePromise = useMemo(async () => {
    return loadStripe(config.publishableKey, {
      stripeAccount: config.connectedAccountId ?? undefined, // This property does not accept `null`
    });
  }, [config.publishableKey, config.connectedAccountId]);

  return (
    <ExpressCheckoutContext.Provider
      value={{
        availablePaymentMethods: availablePaymentMethods,
        hasPaymentMethods: availablePaymentMethods.length > 0,
        isLoadingExpressCheckoutElement: isLoading,
      }}
    >
      <Elements
        stripe={stripePromise}
        options={{
          ...options,
          currency: organization.currency.toLowerCase(),
          amount: 1000, // $10 default amount, but the user does not see this.
        }}
      >
        {/* This Stripe checkout element instance is used to determine what express payment methods
       are available on the user's device. It is hidden from the UI becuase we do not want to 
       show the buttons at this stage, only to get the information returned from it. */}
        {isLoading && (
          <Box display="none">
            <ExpressCheckoutElement
              onConfirm={() => {
                // required for typescript but don't do anything
              }}
              onReady={(e) => {
                checkoutDebug("stripe express checkout element ready", e);

                const paymentMethods = sortedPaymentMethods(
                  e.availablePaymentMethods
                );

                // Use to test specific payment methods
                const testPaymentMethods: PaymentMethod[] = [
                  "applePay",
                  "googlePay",
                  // "paypal",
                  // "link",
                  // "klarna",
                  // "amazonPay",
                ];

                // setAvailablePaymentMethods(testPaymentMethods);
                setAvailablePaymentMethods(paymentMethods);
                setIsLoading(false);

                if (paymentMethods.length === 0) {
                  console.log("No express payment methods available");
                }
              }}
              // TODO: Handle error
              onLoadError={console.error}
            />
          </Box>
        )}
        {children}
      </Elements>
    </ExpressCheckoutContext.Provider>
  );
};

function useExpressCheckoutContext() {
  const context = useContext(ExpressCheckoutContext);
  const { expressCheckoutEnabled } = useStripeConfigContext();

  if (context === undefined && expressCheckoutEnabled) {
    console.error(
      "useExpressCheckoutContext must be used within an ExpressCheckoutProvider"
    );
  }

  return context;
}

export {
  ExpressCheckoutProvider,
  ExpressCheckoutContext,
  useExpressCheckoutContext,
};

const paymentMethodOrder: PaymentMethod[] = [
  "applePay",
  "googlePay",
  "paypal",
  "link",
  "klarna",
  "amazonPay",
];

function sortedPaymentMethods(
  availablePaymentMethods: AvailablePaymentMethods
): PaymentMethod[] {
  return Object.entries(availablePaymentMethods || {})
    .filter(([, available]) => available)
    .map(([method]) => method)
    .sort((a: PaymentMethod, b: PaymentMethod) => {
      const indexA = paymentMethodOrder.indexOf(a);
      const indexB = paymentMethodOrder.indexOf(b);

      // If either method isn't in the order array, put it at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    }) as PaymentMethod[];
}

export function MaybeExpressCheckoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { expressCheckoutEnabled } = useStripeConfigContext();

  if (expressCheckoutEnabled) {
    return (
      <ExpressCheckoutProvider>
        {children} <ExpressCheckoutModal />
      </ExpressCheckoutProvider>
    );
  }
  return <>{children}</>;
}
